import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFeatureFlag } from 'context/feature-flag';
import { isDropoffStatus, isFinishedStatus } from 'common/utils/status';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import Map from './map';
import DropOffMarker from './marker/dropoff';
import AddInstructionBox from './add-instruction-box';
import PickUpMarker from './marker/pickup';
import DriverMarker from './marker/driver';
import RouteLayer from './route-layer';
import useBreakPointValue from '../../hooks/useBreakPointValue';

const SMALL_SCREEN = { top: 20, bottom: 420, left: 32, right: 32 };
const BIG_SCREEN = { top: 50, bottom: 50, left: 400, right: 50 };

const TrackingMap = ({
  allPoints,
  locations,
  driver,
  steps,
  guid,
  status,
  userType,
}) => {
  const {
    hideAddInstruction: hideAddInstructionFeatureFlag,
  } = useFeatureFlag();
  const { isSender } = userType;

  const fitBoundsPadding = useBreakPointValue({
    base: SMALL_SCREEN,
    sm: SMALL_SCREEN,
    md: BIG_SCREEN,
    lg: BIG_SCREEN,
    xl: BIG_SCREEN,
    '2xl': BIG_SCREEN,
  });

  const hideAddInstruction = useMemo(
    () => isFinishedStatus(status) || hideAddInstructionFeatureFlag,
    [status, hideAddInstructionFeatureFlag],
  );

  return (
    <Map
      fitBounds={_isEmpty(allPoints) ? null : allPoints}
      fitBoundsPadding={fitBoundsPadding}
    >
      {_get(locations, 'destinations', []).map(
        (destination, index) =>
          _get(destination, ['coordinates'], false) && (
            <DropOffMarker
              key={`${destination.coordinates[0]}-${
                destination.coordinates[1]
              }`}
              coordinates={destination.coordinates}
              level={locations.destinations.length > 1 ? index + 1 : 0}
            />
          ),
      )}

      {_get(locations, 'destinations', []).map(
        destination =>
          !hideAddInstruction &&
          !isSender &&
          !_get(destination, ['instruction'], false) &&
          _get(destination, ['coordinates'], false) && (
            <AddInstructionBox
              key={`${destination.coordinates[0]}-${
                destination.coordinates[1]
              }`}
              coordinates={destination.coordinates}
              title={destination.name}
              address={destination.address}
              guid={guid}
            />
          ),
      )}

      {isSender &&
        !isDropoffStatus(status) &&
        !hideAddInstruction &&
        _get(locations, ['origin', 'name'], false) &&
        _get(locations, ['origin', 'coordinates'], false) &&
        !_get(locations, ['origin', 'instruction'], false) && (
          <AddInstructionBox
            coordinates={locations.origin.coordinates}
            title={locations.origin.name}
            address={locations.origin.address}
            guid={guid}
          />
        )}

      {_get(locations, ['origin', 'coordinates'], false) && (
        <PickUpMarker coordinates={locations.origin.coordinates} />
      )}

      {_get(driver, ['coordinates'], false) && (
        <DriverMarker
          coordinates={driver.coordinates}
          bearing={driver.bearing}
          type={driver.type}
        />
      )}

      <RouteLayer steps={steps} width={4} />
    </Map>
  );
};

TrackingMap.propTypes = {
  allPoints: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
      instruction: PropTypes.string,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    destinations: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        address: PropTypes.string,
        instruction: PropTypes.string,
        coordinates: PropTypes.arrayOf(PropTypes.number),
      }),
    ),
  }),
  driver: PropTypes.shape({
    bearing: PropTypes.number,
    location: PropTypes.arrayOf(PropTypes.number),
  }),
  steps: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  guid: PropTypes.string,
  status: PropTypes.string,
  userType: PropTypes.shape({
    isSender: PropTypes.bool,
    isReceiver: PropTypes.bool,
  }),
};

TrackingMap.defaultProps = {
  allPoints: null,
  locations: {
    origin: {
      name: '',
      address: '',
      instruction: '',
      coordinates: '',
    },
    destinations: [],
  },
  driver: {
    bearing: 0,
    coordinates: null,
  },
  steps: [],
  guid: '',
  status: '',
  userType: { isSender: false, isReceiver: false },
};

export default TrackingMap;
